/*
This renders only on the first time the user logs in for setup.
*/
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import { useFont } from "../../contexts/FontContext/FontContext";
import { useNavigate } from "react-router-dom";
import TextInput from "../TextInput/TextInput";
import UtilityButton from "../UtilityButton/UtilityButton";
import BackButton from "../BackButton/BackButton";
import axios from "axios";
import styles from "./SetupIntegrationPartner.module.css";

const SetupIntegrationPartner = ( { csrfToken } ) => {

    const [apiKey, setApiKey] = useState(null);
    const [buttonsEnabled, setButtonsEnabled] = useState(false);  // Track if buttons are done rendering and can be enabled
    const [businessName, setBusinessName] = useState('');
    const [businessNameInputValue, setBusinessNameInputValue] = useState(''); 
    const [companyNameInputValue, setCompanyNameInputValue] = useState(''); 
    const [businessStreetOneInputValue, setBusinessStreetOneInputValue] = useState(''); 
    const [businessStreetTwoInputValue, setBusinessStreetTwoInputValue] = useState(''); 
    const [cityInputValue, setCityInputValue] = useState('');
    const [countryInputValue, setCountryInputValue] = useState('');
    const [fadeClassGetStarted, setFadeClassGetStarted] = useState("fade_in");
    const [fadeClassSelectPlatform, setFadeClassSelectPlatform] = useState("hidden");
    const [fadeClassBusinessName, setFadeClassBusinessName] = useState("hidden");
    const [fadeClassShopify, setFadeClassShopify] = useState("no_opacity");
    const [fadeClassMagento, setFadeClassMagento] = useState("no_opacity");
    const [fadeClassWooCommerce, setFadeClassWooCommerce] = useState("no_opacity");
    const [formattedStoreUrl, setFormattedStoreUrl] = useState('');
    const [hoveredPlatform, setHoveredPlatform] = useState(null); // State to track hover
    const [platformSelected, setPlatformSelected] = useState(null);   
    const [platformConnected, setPlatformConnected] = useState(null);
    const [stateInputValue, setStateInputValue] = useState('');
    const [storeName, setStoreName] = useState('');
    const [error, setError] = useState(null);
    const [zipCodeInputValue, setZipCodeInputValue] = useState('');
    const fontSchemes = useFont();    
    const fontFamilyOne = fontSchemes.scheme_one;
    const apiUrl = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();

    useEffect(() => {
        // Set fade out effect after 3 seconds
        const getStartedTimer = setTimeout(() => {
            setFadeClassGetStarted("fade_out");
        }, 2500);
        const businessTitleTimer = setTimeout(() => {
            setFadeClassGetStarted("hidden");
            setFadeClassBusinessName("fade_in");
        }, 4500);
        // Clean up the timer if the component unmounts
        return () => {
            clearTimeout(getStartedTimer);
            clearTimeout(businessTitleTimer);
        }
    }, []);

    // Dynamically update the formatted store URL (this will have to change once we allow for new integration partners)
    useEffect(() => {
        if (storeName.trim()) { setFormattedStoreUrl(`${storeName}.myshopify.com`); } 
        else { setFormattedStoreUrl(''); }
    }, [storeName]);

    useEffect(() => {
        if (businessName) { fadeInPlatformOptions(); }
    }, [businessName]);

    const fadeInPlatformOptions = () => {
        setFadeClassSelectPlatform("fade_in"); 
        setTimeout(() => { setFadeClassShopify("fade_in_bounce"); }, 500);
        setTimeout(() => { setFadeClassWooCommerce("fade_in_bounce"); }, 800);
        setTimeout(() => { 
            setFadeClassMagento("fade_in_bounce"); 
            setButtonsEnabled(true);
        }, 1100);
    }

    const highlightPlatform = (platform) => { setHoveredPlatform(platform); } // Update state when hovered

    // Handle the form submission without reloading the page
    const handleSubmitBusinessDetails = (e) => {
        e.preventDefault(); // Prevent form from reloading the page
        if (businessNameInputValue.trim()) { setBusinessName(businessNameInputValue); } // Set business name if input is not empty
        if (businessStreetOneInputValue.trim()) { setBusinessStreetOneInputValue(businessStreetOneInputValue); } // Set business address

    };

    // Define dynamic styles for border when hovered
    const getBorderStyle = (platform, color) => {
        return platformSelected === platform || hoveredPlatform === platform
            ? { border: `4px solid ${color}` } // Change the border when hovered
            : { border: "1px solid transparent" }; // Default transparent border. Needs border styling to apply transition
    };

    //Asks user to confirm integration partner selection
    const selectPlatform = (platform) => {
        if (platform === "magento" || platform === "woo_commerce") { return; }
        setPlatformSelected(platform);
        if (platform === "shopify") {
            setFadeClassSelectPlatform("hidden");
            setFadeClassMagento("no_opacity");
            setFadeClassWooCommerce("no_opacity")
            setFadeClassShopify(styles.reposition);
        } else if (!platform) { 
            setFadeClassWooCommerce("no_opacity")
            setFadeClassMagento("no_opacity");
            setFadeClassShopify("no_opacity");
            setButtonsEnabled(false);
            fadeInPlatformOptions();
        } else { console.log("not available at this time"); }
    }

    const connectPlatform = async () => {
        console.log("Sending business and platform data");
        try {
            const requestData = {
                business_name: businessName,
                company_name: companyNameInputValue,
                street1: businessStreetOneInputValue,
                street2: businessStreetTwoInputValue,
                city: cityInputValue,
                state: stateInputValue,
                zip_code: zipCodeInputValue,
                country: countryInputValue,
                integration_partner: platformSelected // Pass platform (shopify, magento, etc.)
            };
            const token = localStorage.getItem("authToken");
            // Send a POST request to set the business name and integration partner
            const response = await axios.post(`${apiUrl}/api/business_setup/`, requestData, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
            if (response.status === 200) {
                console.log("Business created/updated successfully:", response.data);
                setPlatformConnected(true);
                setApiKey(response.data.api_key);
                //connect to shopify
            } else { console.error("Failed to create/update business:", response); }
        } catch (error) { console.error("Error creating/updating business:", error.response ? error.response.data : error.message); }
    };

    //make request to backend to see if provided store name exists
    const handleValidateStore = async () => {
        try {
            const user = localStorage.getItem("user")
            const response = await axios.get(`${apiUrl}/api/validate_shopify_store/`, { params: { store_name: storeName, username: user } });
            if (response.data.valid) {
                setError(null);
                return true; // Return true if the store is valid
            } else {
                setError("Store not found");
                return false;
            }
        } catch (err) {
            console.error("Error validating store:", err);
            setError("An error occurred while validating the store.");
            return false;
        }
    };

    const handleConnect = async (e) => {
        e.preventDefault();
        const isValid = await handleValidateStore(); // Wait for store validation
        if (!isValid) { return; } // If store is not valid, stop here
        navigate("/home");
    }

    return (
        <div className={styles.setup_integration_partner_container}>
            <Helmet>
                <title>Integration</title>
            </Helmet>
            <LogoWrapper class_={styles.setup_integration_partner_logo} src={`${process.env.PUBLIC_URL}/images/partial_logo.png`} />
            {!businessName && (
                <form className={styles.get_started_container} onSubmit={handleSubmitBusinessDetails}>
                    <p className={`${styles.get_started_title} titles ${fontFamilyOne} ${fadeClassGetStarted}`}>Let's get started.</p>
                    <p className={`${styles.business_name_title} titles ${fontFamilyOne} ${fadeClassBusinessName}`}>Enter company name and address.</p>
                    {fadeClassBusinessName !== "hidden" && (
                        <div className={styles.address_inputs_container}>
                            <TextInput inputType="business_name" value={businessNameInputValue} onChange={(e) => setBusinessNameInputValue(e.target.value)} required error={false} fadeIn={true} />
                            <TextInput inputType="company_name" value={companyNameInputValue} onChange={(e) => setCompanyNameInputValue(e.target.value)} error={false} fadeIn={true} />
                            <TextInput inputType="street1" value={businessStreetOneInputValue} onChange={(e) => setBusinessStreetOneInputValue(e.target.value)} required error={false} fadeIn={true} />
                            <TextInput inputType="street2" value={businessStreetTwoInputValue} onChange={(e) => setBusinessStreetTwoInputValue(e.target.value)} error={false} fadeIn={true} />
                            <TextInput inputType="city" value={cityInputValue} onChange={(e) => setCityInputValue(e.target.value)} required error={false} fadeIn={true} />
                            <TextInput inputType="state" value={stateInputValue} onChange={(e) => setStateInputValue(e.target.value)} required error={false} fadeIn={true} />
                            <TextInput inputType="zip_code" value={zipCodeInputValue} onChange={(e) => setZipCodeInputValue(e.target.value)} required error={false} fadeIn={true} />
                            <TextInput inputType="country" value={countryInputValue} onChange={(e) => setCountryInputValue(e.target.value)} required error={false} fadeIn={true} />
                            <button className={`login_buttons ${fontFamilyOne} ${fadeClassBusinessName}`} type="submit">Next</button>
                        </div>
                    )}
                </form>
            )}
            {businessName && !platformConnected && (
                <div className={styles.integrations_container}>
                    <p className={`titles ${styles.integration_partner_title} ${fontFamilyOne} ${fadeClassSelectPlatform}`}>Select platform to integrate.</p> 
                    <div className={styles.integration_options_wrapper}>
                        <UtilityButton id="woo_commerce_integration_select" imageId={styles.woo_commerce_integration_logo} title="WooCommerce" option="woo_commerce" imageSrc={`${process.env.PUBLIC_URL}/images/woo_commerce.png`} onHover={highlightPlatform} onLeave={() => setHoveredPlatform(null)} onClick={selectPlatform} fadeClass={`${fadeClassWooCommerce} ${styles.woo_commerce_select}`} isSelected={platformSelected === "woo_commerce"} isDisabled={!buttonsEnabled} borderStyle={getBorderStyle("woo_commerce", "#8359b6")}/>
                        <UtilityButton id="shopify_integration_select" imageId={styles.shopify_integration_logo} title="Shopify" option="shopify" imageSrc={`${process.env.PUBLIC_URL}/images/shopify.png`} onHover={highlightPlatform} onLeave={() => setHoveredPlatform(null)} onClick={selectPlatform} fadeClass={`${fadeClassShopify} ${styles.shopify_integration_select}`} isSelected={platformSelected === "shopify"} isDisabled={!buttonsEnabled} borderStyle={getBorderStyle("shopify", "#98c14c")}/>
                        <UtilityButton id="magento_integration_select" imageId={styles.magento_integration_logo} title="Magento" option="magento" imageSrc={`${process.env.PUBLIC_URL}/images/magento.png`} onHover={highlightPlatform} onLeave={() => setHoveredPlatform(null)} onClick={selectPlatform} fadeClass={`${fadeClassMagento} ${styles.magento_integration_select}`} isSelected={platformSelected === "magento"} isDisabled={!buttonsEnabled} borderStyle={getBorderStyle("magento", "#f36729")}/>
                    </div>
                    {platformSelected && (
                        <div className={styles.platform_selected_wrapper}>
                            <button onClick={() => connectPlatform()} className={`login_buttons ${fontFamilyOne}`} style={{width: "200px" }} type="submit">connect {platformSelected}</button>                         
                            <BackButton button_id={styles.integration_select_back_button} image_id={styles.integration_select_back_button_arrow} />
                        </div>
                    )}
                </div>
            )}
            {businessName && platformConnected && (
                <form className={styles.connect_platform_container} onSubmit={handleConnect}>
                    <p className={`titles ${styles.select_platform_title} ${fontFamilyOne}`}>Enter {platformSelected} store name.</p>
                    {error && (<p className={`error_message ${styles.connect_store_error_message}`}>{error}</p>)}
                    <div className={styles.shopify_input_container}>
                        <TextInput inputType="shopify_store_name" placeholder="Store name" value={storeName} onChange={(e) => setStoreName(e.target.value)} required fadeIn={true} />
                        {storeName && (<p className={styles.store_url_preview}>{formattedStoreUrl}</p>)} 
                    </div>
                    <button className={`login_buttons ${styles.connect_platform_submit} ${fontFamilyOne}`} type="submit">Connect Store</button>
                </form>
            )}
        </div>
    );
};

export default SetupIntegrationPartner;
